import React, { useState, useEffect, useRef } from 'react';

interface PerformanceMonitorProps {
  show?: boolean;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({ 
  show = false, 
  position = 'top-right' 
}) => {
  const [fps, setFps] = useState(0);
  const [avgFps, setAvgFps] = useState(0);
  const lastTimeRef = useRef<number>(0);
  const frameCountRef = useRef(0);
  const fpsHistoryRef = useRef<number[]>([]);
  const animationRef = useRef<number>();

  useEffect(() => {
    if (!show) return;

    const measureFPS = (currentTime: number) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = currentTime;
      }

      const delta = currentTime - lastTimeRef.current;
      frameCountRef.current++;

      // Atualizar FPS a cada 500ms
      if (delta >= 500) {
        const currentFPS = Math.round((frameCountRef.current * 1000) / delta);
        setFps(currentFPS);

        // Manter histórico de FPS para calcular média
        fpsHistoryRef.current.push(currentFPS);
        if (fpsHistoryRef.current.length > 20) {
          fpsHistoryRef.current.shift();
        }

        const average = Math.round(
          fpsHistoryRef.current.reduce((a, b) => a + b, 0) / fpsHistoryRef.current.length
        );
        setAvgFps(average);

        frameCountRef.current = 0;
        lastTimeRef.current = currentTime;
      }

      animationRef.current = requestAnimationFrame(measureFPS);
    };

    animationRef.current = requestAnimationFrame(measureFPS);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [show]);

  if (!show) return null;

  const getPositionStyle = () => {
    const baseStyle = {
      position: 'fixed' as const,
      padding: '8px 12px',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      color: 'white',
      fontSize: '12px',
      fontFamily: 'monospace',
      borderRadius: '4px',
      zIndex: 10000,
      pointerEvents: 'none' as const
    };

    switch (position) {
      case 'top-left':
        return { ...baseStyle, top: '10px', left: '10px' };
      case 'top-right':
        return { ...baseStyle, top: '10px', right: '10px' };
      case 'bottom-left':
        return { ...baseStyle, bottom: '10px', left: '10px' };
      case 'bottom-right':
        return { ...baseStyle, bottom: '10px', right: '10px' };
    }
  };

  const getFpsColor = (fps: number) => {
    if (fps >= 55) return '#00ff00'; // Verde
    if (fps >= 30) return '#ffff00'; // Amarelo
    return '#ff0000'; // Vermelho
  };

  return (
    <div style={getPositionStyle()}>
      <div>
        FPS: <span style={{ color: getFpsColor(fps) }}>{fps}</span>
      </div>
      <div>
        Avg: <span style={{ color: getFpsColor(avgFps) }}>{avgFps}</span>
      </div>
      <div style={{ fontSize: '10px', marginTop: '4px' }}>
        Cores: {navigator.hardwareConcurrency || 'N/A'} | 
        RAM: {(navigator as any).deviceMemory || 'N/A'}GB
      </div>
    </div>
  );
};

export default PerformanceMonitor;
